import { Badge, Button, Field, Input, InputArea, Select } from '@cloudflare/kumo';
import { useCallback, useEffect, useState } from 'react';
import { DangerButton } from '../../components/ui/DangerButton';
import { ICONS } from '../../components/ui/icons';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { useAppToast } from '../../hooks/useAppToast';

interface Expectations { contains?: string[]; notContains?: string[]; tool?: string; handoff?: boolean }
interface LastResult { pass: boolean; reply: string; tools: string[]; handoff: boolean; failures: string[]; at: string }
interface TestCase { id: string; name: string; input: string; expectations: Expectations; lastResult: LastResult | null }
interface Form { id?: string; name: string; input: string; contains: string; notContains: string; tool: string; handoff: '' | 'sim' | 'nao' }

const emptyForm = (): Form => ({ name: '', input: '', contains: '', notContains: '', tool: '', handoff: '' });
const list = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);

// O `Select` do Cloudscape não aceita valor vazio: 'tanto faz' vira uma sentinela.
const HANDOFF_ANY = '__any__';
const HANDOFF_OPTIONS = [
  { value: HANDOFF_ANY, label: 'tanto faz' },
  { value: 'sim', label: 'precisa passar' },
  { value: 'nao', label: 'não pode passar' },
];

function describe(e: Expectations) {
  const parts: string[] = [];
  if (e.contains?.length) parts.push(`traz ${e.contains.map((c) => `"${c}"`).join(', ')}`);
  if (e.notContains?.length) parts.push(`não traz ${e.notContains.map((c) => `"${c}"`).join(', ')}`);
  if (e.tool) parts.push(`usa ${e.tool}`);
  if (e.handoff === true) parts.push('passa para a equipe');
  if (e.handoff === false) parts.push('não passa para a equipe');
  return parts.join(' · ') || 'só precisa responder';
}

/**
 * Casos de teste do agente: mensagens de cliente e o que a resposta precisa ter.
 * Rodam com a configuração salva, em simulação. Publicar roda todos antes.
 */
export function AgentTestCasesPanel({ agentId, canEdit }: { agentId: string; canEdit: boolean }) {
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const [cases, setCases] = useState<TestCase[]>([]);
  const [form, setForm] = useState<Form | null>(null);
  const [running, setRunning] = useState<string | null>(null);

  const load = useCallback(async () => {
    const r = await fetchApi(`/api/ai/agents/${agentId}/tests`);
    if (r.ok) setCases((await r.json()).cases);
  }, [agentId, fetchApi]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form) return;
    const expectations: Expectations = {
      contains: list(form.contains), notContains: list(form.notContains),
      ...(form.tool.trim() ? { tool: form.tool.trim() } : {}),
      ...(form.handoff ? { handoff: form.handoff === 'sim' } : {}),
    };
    const r = await fetchApi(form.id ? `/api/ai/agents/${agentId}/tests/${form.id}` : `/api/ai/agents/${agentId}/tests`, {
      method: form.id ? 'PUT' : 'POST', body: JSON.stringify({ name: form.name, input: form.input, expectations }),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { toast.error(j.message || 'Não foi possível salvar o caso'); return; }
    setForm(null); load();
  };

  const run = async (ids?: string[]) => {
    setRunning(ids?.[0] ?? 'all');
    const r = await fetchApi(`/api/ai/agents/${agentId}/tests/run`, { method: 'POST', body: JSON.stringify(ids ? { ids } : {}) });
    const j = await r.json().catch(() => ({}));
    setRunning(null);
    if (!r.ok) { toast.error(j.message || 'Não foi possível rodar'); return; }
    if (j.failed) toast.error(`${j.passed} passou, ${j.failed} falhou`); else toast.success(`${j.passed} caso${j.passed === 1 ? '' : 's'} passou`);
    load();
  };

  const remove = async (id: string) => {
    const r = await fetchApi(`/api/ai/agents/${agentId}/tests/${id}`, { method: 'DELETE' });
    if (r.ok) load();
  };

  const edit = (c: TestCase) => setForm({
    id: c.id, name: c.name, input: c.input, contains: (c.expectations.contains ?? []).join(', '), notContains: (c.expectations.notContains ?? []).join(', '),
    tool: c.expectations.tool ?? '', handoff: c.expectations.handoff === undefined ? '' : c.expectations.handoff ? 'sim' : 'nao',
  });

  return (
    <section className="space-y-3" data-testid="ai-agent-tests">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-lg font-semibold" style={{ fontSize: 16 }}>Casos de teste</h3>
        <div className="flex gap-2">
          {cases.length > 0 && canEdit && <Button variant="secondary" icon={ICONS.Play} loading={running === 'all'} disabled={!!running} onClick={() => run()} data-testid="ai-agent-tests-run">Rodar todos</Button>}
          {canEdit && !form && <Button variant="secondary" icon={ICONS.Plus} onClick={() => setForm(emptyForm())} data-testid="ai-agent-tests-new">Novo caso</Button>}
        </div>
      </div>
      <div className="text-xs !text-[var(--text-muted)]">
        Mensagens de cliente e o que a resposta precisa ter. Rodam com a configuração salva, em simulação (nada é gravado). Publicar roda todos e para se algum falhar.
      </div>

      {form && (
        <div className="space-y-2 p-3" style={{ border: '1px solid var(--border)', borderRadius: 8 }}>
          <Field label="Nome (opcional)">
            <Input onChange={(e) => setForm({ ...form, name: e.target.value })} value={form.name} placeholder="Pergunta sobre horário" />
          </Field>
          <InputArea label="Mensagem do cliente" onChange={(e) => setForm({ ...form, input: e.target.value })} rows={2} value={form.input} />
          <Field label="A resposta precisa trazer" description="Separe por vírgula. Sem diferença de acento ou maiúscula.">
            <Input onChange={(e) => setForm({ ...form, contains: e.target.value })} value={form.contains} placeholder="9h, segunda a sexta" />
          </Field>
          <Field label="A resposta não pode trazer">
            <Input onChange={(e) => setForm({ ...form, notContains: e.target.value })} value={form.notContains} placeholder="cartão, senha" />
          </Field>
          <Field label="Ferramenta que precisa usar (opcional)">
            <Input onChange={(e) => setForm({ ...form, tool: e.target.value })} value={form.tool} placeholder="transferir_humano" />
          </Field>
          <Field label="Passar para a equipe">
            <Select value={(HANDOFF_OPTIONS.find((o) => o.value === (form.handoff || HANDOFF_ANY)) ?? null)?.value ?? null} items={HANDOFF_OPTIONS} onValueChange={(value: string | null) => {
                const v = value;
                setForm({ ...form, handoff: v === HANDOFF_ANY || !v ? '' : (v as Form['handoff']) });
              }} />
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setForm(null)}>Cancelar</Button>
            <Button variant="primary" disabled={!form.input.trim()} onClick={save} data-testid="ai-agent-tests-save">Salvar caso</Button>
          </div>
        </div>
      )}

      {cases.length === 0 && !form && <div className="text-sm !text-[var(--text-muted)]">Nenhum caso ainda. Comece pelas perguntas mais comuns e pelo que o agente nunca pode fazer.</div>}
      <ul className="space-y-2">
        {cases.map((c) => (
          <li key={c.id} className="p-3 space-y-1" style={{ border: '1px solid var(--border)', borderRadius: 8 }} data-testid={`ai-agent-test-case-${c.id}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="font-semibold">{c.name}</div>
              {c.lastResult ? <Badge variant={c.lastResult.pass ? 'green' : 'red'}>{c.lastResult.pass ? 'passou' : 'falhou'}</Badge> : <Badge variant="neutral">não rodou</Badge>}
            </div>
            <div className="text-sm">“{c.input}”</div>
            <div className="text-xs !text-[var(--text-muted)]">Espera: {describe(c.expectations)}</div>
            {c.lastResult && !c.lastResult.pass && c.lastResult.failures.map((f) => <div className="text-xs !text-[var(--crm-text-error)]" key={f}>✗ {f}</div>)}
            {c.lastResult?.reply && (
              <details className="text-xs"><summary style={{ cursor: 'pointer' }}>Resposta da última execução</summary>
                <pre style={{ whiteSpace: 'pre-wrap', background: 'var(--surface-2)', padding: 6, borderRadius: 6 }}>{c.lastResult.reply}{c.lastResult.tools.length ? `\n\nferramentas: ${c.lastResult.tools.join(', ')}` : ''}</pre>
              </details>
            )}
            {canEdit && (
              <div className="flex gap-2 pt-1">
                <Button variant="secondary" disabled={!!running} loading={running === c.id} onClick={() => run([c.id])}>Rodar</Button>
                <Button variant="secondary" onClick={() => edit(c)}>Editar</Button>
                <DangerButton variant="dangerOutline" onClick={() => remove(c.id)}>Excluir</DangerButton>
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
